import { useEffect, useState } from 'react';
import type { PublicParticipant } from '@planning-session/shared';
import styles from './HostControls.module.css';

export interface HostControlsProps {
  readonly participants: readonly PublicParticipant[];
  readonly youId: string | null;
  onTransferHost: (participantId: string) => void;
  onRemoveParticipant: (participantId: string) => void;
}

/**
 * The two things only a host can do to a seat: hand the session over, and clear a
 * seat that has gone away and is holding the round open.
 *
 * These live here rather than on the table because the table is inverted, and the
 * field carries what a person reads, never what they click.
 */
export function HostControls({ participants, youId, onTransferHost, onRemoveParticipant }: HostControlsProps) {
  const [confirming, setConfirming] = useState<string | null>(null);

  // A pending confirmation is only meaningful for a beat.
  useEffect(() => {
    if (confirming === null) return;
    const timer = window.setTimeout(() => setConfirming(null), 4_000);
    return () => window.clearTimeout(timer);
  }, [confirming]);

  const others = participants.filter((participant) => participant.id !== youId);
  if (others.length === 0) return null;

  return (
    <div className={styles.people}>
      <span className={styles.label}>People</span>

      <ul className={styles.list}>
        {others.map((participant) => (
          <li key={participant.id} className={styles.row}>
            <span className={styles.name}>{participant.name}</span>
            {!participant.connected && <span className={styles.away}>away</span>}

            <span className={styles.actions}>
              {participant.connected ? (
                // Two-step, because handing the session over is the one action
                // here the person taking it cannot undo for you.
                <button
                  type="button"
                  className={`${styles.action} ${confirming === participant.id ? styles.confirming : ''}`}
                  onClick={() => {
                    if (confirming === participant.id) {
                      onTransferHost(participant.id);
                      setConfirming(null);
                    } else {
                      setConfirming(participant.id);
                    }
                  }}
                >
                  {confirming === participant.id ? 'Confirm' : 'Make host'}
                </button>
              ) : (
                <button type="button" className={styles.action} onClick={() => onRemoveParticipant(participant.id)}>
                  Clear seat
                </button>
              )}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
